import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useEffect, useState } from 'react';
import LandingPage from './pages/LandingPage';
import BuildPage from './pages/BuildPage';
import PreviewPage from './pages/PreviewPage';
import FlightItineraryPage from './pages/templates/FlightItineraryPage';
import HolidayPlannerPage from './pages/templates/HolidayPlannerPage';
import PdfEditorPage from './pages/templates/PdfEditorPage';
import InvoiceGeneratorPage from './pages/templates/InvoiceGeneratorPage';
import QRCodeGeneratorPage from './pages/templates/QRCodeGeneratorPage';
import ResumeBuilderPage from './pages/templates/ResumeBuilderPage';
import AdminLeadsPage from './pages/AdminLeadsPage';
import PricingPage from './pages/PricingPage';
import TermsPage from './pages/TermsPage';
import PrivacyPage from './pages/PrivacyPage';
import ContactPage from './pages/ContactPage';
import AboutPage from './pages/AboutPage';
import SoulLibraryPage from './pages/SoulLibraryPage';
import SoulDetailPage from './pages/SoulDetailPage';
import MySoulsPage from './pages/MySoulsPage';
import AuthorPage from './pages/AuthorPage';
import SubscribeSuccessPage from './pages/SubscribeSuccessPage';
import NotFoundPage from './pages/NotFoundPage';
import AuthCallback from './components/AuthCallback';
import './App.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * React Router 6 can be fussy with `@` in path patterns, so we handle /@handle via a
 * single-segment catch route that dispatches to AuthorPage when the slot starts with @.
 */
function SingleSegmentRoute() {
  const location = useLocation();
  const raw = location.pathname.replace(/^\//, '');
  if (raw.startsWith('@')) {
    return <AuthorPage />;
  }
  return <NotFoundPage />;
}

function RequireAuth({ children }) {
  const location = useLocation();
  const [state, setState] = useState(() => {
    // If user was just handed in via state (from AuthCallback), skip check
    if (location.state?.user) return { checked: true, ok: true };
    // If cached user exists, optimistically show — /auth/me will re-verify
    const cached = localStorage.getItem('atw_user');
    if (cached) return { checked: true, ok: true };
    return { checked: false, ok: false };
  });

  useEffect(() => {
    // Skip if just came from OAuth callback
    if (location.state?.user) return;
    // Verify with backend
    (async () => {
      try {
        const r = await fetch(`${API}/auth/me`, { credentials: 'include' });
        if (r.ok) {
          const u = await r.json();
          localStorage.setItem('atw_user', JSON.stringify(u));
          setState({ checked: true, ok: true });
        } else {
          // Only redirect if we don't already have cached user
          if (!localStorage.getItem('atw_user')) {
            setState({ checked: true, ok: false });
          }
        }
      } catch {
        setState({ checked: true, ok: !!localStorage.getItem('atw_user') });
      }
    })();
  }, [location.state]);

  if (!state.checked) return null;
  if (!state.ok) return <Navigate to="/" replace />;
  return children;
}

function AppRouter() {
  const location = useLocation();
  // Handle OAuth callback synchronously during render
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/preview/:variant" element={<PreviewPage />} />
      <Route path="/templates/flight-itinerary" element={<FlightItineraryPage />} />
      <Route path="/templates/holiday-planner" element={<HolidayPlannerPage />} />
      <Route path="/templates/pdf-editor" element={<PdfEditorPage />} />
      <Route path="/templates/invoice" element={<InvoiceGeneratorPage />} />
      <Route path="/templates/qr-code" element={<QRCodeGeneratorPage />} />
      <Route path="/templates/resume" element={<ResumeBuilderPage />} />
      <Route path="/admin/leads" element={<AdminLeadsPage />} />
      <Route path="/pricing" element={<PricingPage />} />
      <Route path="/terms" element={<TermsPage />} />
      <Route path="/privacy" element={<PrivacyPage />} />
      <Route path="/contact" element={<ContactPage />} />
      <Route path="/about" element={<AboutPage />} />
      <Route path="/souls" element={<SoulLibraryPage />} />
      <Route path="/souls/mine" element={<MySoulsPage />} />
      <Route path="/subscribe/success" element={<SubscribeSuccessPage />} />
      <Route path="/@:handle" element={<AuthorPage />} />
      <Route path="/:handleSlot" element={<SingleSegmentRoute />} />
      <Route path="/souls/:slug" element={<SoulDetailPage />} />
      <Route
        path="/build"
        element={
          <RequireAuth>
            <BuildPage />
          </RequireAuth>
        }
      />
      <Route path="/preview/builder" element={<BuildPage />} />
      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AppRouter />
      </BrowserRouter>
    </div>
  );
}

export default App;
